"use client";

import { forwardRef, useState } from "react";
import { cn } from "~/lib/utils";
import { ChevronDownIcon } from "~/components/icons";

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}

const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({ className, options, value, onChange, placeholder = "Select...", disabled = false, size = "lg", ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = options.find(opt => opt.value === value);

    const handleSelect = (optionValue: string) => {
      onChange?.(optionValue);
      setIsOpen(false);
    };

    const sizeClasses = {
      sm: "h-8 px-3 text-sm rounded",
      md: "h-10 px-3 text-sm rounded-md",
      lg: "h-[60px] px-4 text-base rounded-md",
    };

    const iconSizeClasses = {
      sm: "h-4 w-4",
      md: "h-4 w-4",
      lg: "h-5 w-5",
    };

    return (
      <div ref={ref} className={cn("relative", className)} {...props}>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between border border-[#d9d9e0] bg-white text-left leading-6 text-[#1c2024] transition-colors hover:border-[#8b8d98] focus:border-[#1c3144] focus:outline-none focus:ring-2 focus:ring-[rgba(0,85,199,0.1)] disabled:cursor-not-allowed disabled:opacity-50",
            sizeClasses[size],
            className
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className={cn(!selectedOption && "text-[#60646c]")}>
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronDownIcon className={cn("text-[#60646c]", iconSizeClasses[size])} />
        </button>

        {isOpen && !disabled && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute top-full z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-[#e8e8ec] bg-white shadow-lg">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    "flex w-full items-center px-4 py-3 text-left text-sm text-[#1c2024] transition-colors hover:bg-[#f9f9fb]",
                    value === option.value && "bg-[rgba(0,85,199,0.04)] font-semibold"
                  )}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export { Select };

